/**
 * Processing-board pieces on top of the gate V1 kit (features/gate/v1/kit.tsx): the header with
 * the 5-step dots, the green "saved" line, numbered picture steps, the read-only weight box, the
 * tag and sack chips, the summary rows, the phone-for-help sheet and the "no site list" screen.
 * Values (sizes, colours, radii) are the alignment map's F6 V1 boards'.
 */
import { useState, type ReactNode } from 'react'
import { useNetworkStatus } from '@/api'
import { useI18n } from '@/i18n'
import { downloadCaches, useSyncStatus } from '@/offline'
import {
  Card,
  Cta,
  Footer,
  Icon,
  SheetActions,
  Btn,
  StepDots,
  V1Header,
  V1Screen,
  V1Sheet,
  WaitCta,
  type HeaderFarmer,
} from '@/features/gate/v1/kit'
import { Rich } from '@/features/gate/v1/Rich'
import { Refused } from '@/features/gate/v1/scan'
import { C } from '@/features/gate/v1/style'
import { cropName, cropWord, rosterShort, tr } from '@/features/gate/v1/text'
import type { ProcessState, RunView } from '../projection'
import { useProcessState } from '../useProcess'
import { PIcon, ProcessSprite, type PIconName } from './icons'

const FONT = "'Mukta', sans-serif"

// ---------------------------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------------------------

/** The projected site, re-drawn when the language changes (tr() reads the language on screen). */
export function useSite() {
  useI18n()
  return useProcessState()
}

/** "नं. 112" and the crop under it (the site list carries no farmer names). */
export function runFarmer(
  run: Pick<RunView, 'farmer_code' | 'crop_id' | 'crop_name' | 'stream'>,
): HeaderFarmer {
  // The crop id is a uuid: the word comes from the server's crop name (English).
  const crop = run.crop_name ? cropWord(run.crop_name) : cropName([], run.crop_id)
  return {
    name: run.farmer_code
      ? tr('process.v1.farmerNo', { no: rosterShort(run.farmer_code) })
      : tr('process.v1.ordinary'),
    line: crop,
  }
}

/** A step's name in the language on screen. */
export function stepLabel(state: ProcessState, code: string): string {
  const s = state.steps.get(code)
  if (!s) return code
  return document.documentElement.lang === 'en'
    ? s.en
    : tr('process.v1.lang') === 'hi'
      ? s.hi
      : s.en
}

/** An output's name in the language on screen. */
export function outputLabel(state: ProcessState, stepCode: string, key: string): string {
  const o = state.steps.get(stepCode)?.outputs.find((x) => x.key === key)
  if (!o) return key
  return tr('process.v1.lang') === 'hi' ? o.hi : o.en
}

/** The last part of a code, as people read it ("PR-261104-DEC-02-1" → "02-1"). */
export const tail2 = (code: string) => code.split('-').slice(-2).join('-')
/** "0415" from "KH-0415". */
export const tail1 = (code: string) => code.split(/[-/]/).at(-1) ?? code

// ---------------------------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------------------------

/**
 * The boards' header: back, the farmer, records to send, मदद; then the step icon, the title (one
 * language), the n/5 dots and ⋯.
 */
export function PHeader({
  onBack,
  farmer,
  icon,
  title,
  sub,
  step,
  onMore,
}: {
  onBack?: () => void
  farmer?: HeaderFarmer
  icon: PIconName
  title: string
  /** A quieter line under the title (the operator's name on आज). */
  sub?: string
  /** 1–5 (open, clean, bags in, run, weigh and close). */
  step?: number
  onMore?: () => void
}) {
  const sync = useSyncStatus()
  return (
    <>
      <ProcessSprite />
      <V1Header onBack={onBack} farmer={farmer} pending={sync.pending} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 6px' }}>
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 18,
            background: C.acc,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PIcon name={icon} size={22} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 21, lineHeight: 1.2, fontWeight: 700 }}>{title}</h1>
          {sub && <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.15 }}>{sub}</span>}
        </span>
        {step !== undefined && <StepDots step={step} of={5} />}
        {onMore && (
          <button
            type="button"
            aria-label={tr('gate.v1.a11y.moreOptions')}
            onClick={onMore}
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 20,
              border: `1.5px solid ${C.border}`,
              background: C.surface,
              color: C.ink,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              boxSizing: 'border-box',
              cursor: 'pointer',
            }}
          >
            <Icon name="more" />
          </button>
        )}
      </div>
    </>
  )
}

/** The coloured line under the header (good stock green, red tie red). */
export function PBand({
  tone,
  icon,
  children,
}: {
  tone: 'good' | 'red'
  icon: PIconName
  children: ReactNode
}) {
  return (
    <div
      style={{
        background: tone === 'good' ? C.okWash : C.bad,
        color: tone === 'good' ? C.ok : '#ffffff',
        padding: '5px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      <PIcon name={icon} size={22} />
      <span>{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------------------------

/** The green "saved" line at the top of a screen. */
export function SavedLine({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 12,
        background: C.ok,
        color: '#ffffff',
        fontSize: 17,
        fontWeight: 700,
      }}
    >
      <Icon name="ok" size={22} sw={3} />
      {children}
    </div>
  )
}

/** Green pill with a tick ("अच्छा माल", "इसी रन की", "ली"). */
export function OkPill({ children, size = 15 }: { children: ReactNode; size?: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 4px',
        borderRadius: 16,
        background: C.okWash,
        color: C.ok,
        fontWeight: 700,
        fontSize: size,
        lineHeight: 1.25,
        alignSelf: 'flex-start',
        whiteSpace: 'nowrap',
      }}
    >
      <TickDot size={22} />
      {children}
    </span>
  )
}

export function TickDot({ size = 22, color = C.ok }: { size?: number; color?: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: size / 2,
        background: color,
        color: '#ffffff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name="ok" size={Math.round(size * 0.64)} sw={3} />
    </span>
  )
}

/** The numbered circle of a step list. */
export function NumDot({ n, size = 28 }: { n: number; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: size / 2,
        border: `2px solid ${C.ink}`,
        color: C.ink,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size > 24 ? 16 : 14,
        fontWeight: 700,
        boxSizing: 'border-box',
        lineHeight: 1,
      }}
    >
      {n}
    </span>
  )
}

/** "1 [picture] छलनियाँ **बाहर**, ब्रश" — a numbered step with its picture box. */
export function PictureStep({ n, icon, text }: { n: number; icon: PIconName; text: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <NumDot n={n} />
      <span
        aria-hidden="true"
        style={{
          width: 54,
          height: 44,
          flexShrink: 0,
          borderRadius: 8,
          background: C.photo,
          color: C.photoInk,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PIcon name={icon} size={28} sw={1.6} />
      </span>
      <span style={{ fontSize: 17, lineHeight: 1.3 }}>
        <Rich text={text} />
      </span>
    </div>
  )
}

/** Beige hint with its icon in a white square ("**फोटो** देखकर चुनें"). */
export function Hint({
  icon,
  children,
  tone = 'sunk',
  size = 17,
}: {
  icon: PIconName
  children: ReactNode
  tone?: 'sunk' | 'bad'
  size?: number
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 12,
        background: tone === 'bad' ? C.badWash : C.sunk,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 10,
          background: C.surface,
          color: tone === 'bad' ? C.bad : C.acc,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PIcon name={icon} size={24} />
      </span>
      <span style={{ fontSize: size, lineHeight: 1.35 }}>{children}</span>
    </div>
  )
}

/** The icon in a 44px beige square at the start of a row. */
export function LeadBox({
  icon,
  size = 44,
  iconSize = 26,
  bg = C.sunk,
  color = C.acc,
  radius = 10,
}: {
  icon: PIconName
  size?: number
  iconSize?: number
  bg?: string
  color?: string
  radius?: number
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        background: bg,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <PIcon name={icon} size={iconSize} />
    </span>
  )
}

/** The read-only big weight (पिछला वज़न 38.40 किलो). */
export function WeightBox({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `2px solid ${C.border}`,
        borderRadius: 12,
        padding: '6px 12px 8px',
      }}
    >
      <div style={{ fontSize: 16, color: C.muted, fontWeight: 600, lineHeight: 1.3 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.05 }}>{value}</span>
        <span style={{ fontSize: 20, fontWeight: 600, color: C.muted }}>
          {tr('process.v1.kgUnit')}
        </span>
      </div>
    </div>
  )
}

/** "टैग 0415 · स्कैन हुआ" chip, with the "टैग ख़राब" scissors when given. */
export function TagChip({ code, onBad }: { code: string; onBad?: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: 6,
        borderRadius: 12,
        background: C.surface,
        border: `2px solid ${C.ok}`,
        minWidth: 0,
      }}
    >
      <PIcon name="tag" size={24} color={C.ok} />
      <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
        <b style={{ fontSize: 18, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
          {tr('process.v1.tag', { no: tail1(code) })}
        </b>
        <span style={{ fontSize: 14, color: C.ok, fontWeight: 700, lineHeight: 1.2 }}>
          {tr('process.v1.scanned')}
        </span>
      </span>
      {onBad && (
        <button
          type="button"
          aria-label={tr('process.v1.weigh.badTag')}
          onClick={onBad}
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            border: `1.5px solid ${C.border}`,
            background: C.surface,
            color: C.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          <Icon name="scissors" size={22} />
        </button>
      )}
    </div>
  )
}

/** The site's usual output sack and its tare (no picker in V1). */
export function SackChip({ name, tare }: { name: string; tare: string }) {
  return (
    <div
      aria-label={tr('process.v1.weigh.sackAria', { name, kg: tare })}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 8px',
        borderRadius: 12,
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        color: C.ink,
        minWidth: 0,
      }}
    >
      <PIcon name="sack" size={28} color={C.acc} />
      <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
        <b
          style={{
            fontSize: 18,
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </b>
        <span style={{ fontSize: 14, color: C.muted }}>{tr('process.v1.kg', { kg: tare })}</span>
      </span>
    </div>
  )
}

/** Label · value rows in a white card (totals, the sample's batch). */
export function SumCard({
  rows,
}: {
  rows: Array<{ label: ReactNode; value: ReactNode; bold?: boolean } | 'rule'>
}) {
  return (
    <section
      style={{
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        borderRadius: 12,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {rows.map((r, i) =>
        r === 'rule' ? (
          <div key={i} style={{ borderTop: `1px solid ${C.rule}` }} />
        ) : (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 8,
              fontWeight: r.bold ? 700 : undefined,
            }}
          >
            <span style={{ fontSize: 17 }}>{r.label}</span>
            <span style={{ fontSize: 18, whiteSpace: 'nowrap' }}>{r.value}</span>
          </div>
        ),
      )}
    </section>
  )
}

/** A picked tile (3px ink border and the corner tick), as the kit's Tile, with an optional icon. */
export function PickTile({
  pressed,
  onClick,
  icon,
  children,
  minHeight = 56,
}: {
  pressed: boolean
  onClick: () => void
  icon?: PIconName
  children: ReactNode
  minHeight?: number
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        position: 'relative',
        minHeight,
        borderRadius: 12,
        border: pressed ? `3px solid ${C.ink}` : `1.5px solid ${C.border}`,
        background: C.surface,
        color: C.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 10px',
        gap: 6,
        boxSizing: 'border-box',
        width: '100%',
        fontFamily: FONT,
        cursor: 'pointer',
      }}
    >
      {icon && <PIcon name={icon} size={26} />}
      <b style={{ fontSize: 18, lineHeight: 1.2, textAlign: 'center' }}>{children}</b>
      {pressed && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 24,
            height: 24,
            borderRadius: 12,
            background: C.ink,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="ok" size={15} sw={3} />
        </span>
      )}
    </button>
  )
}

export function Grid2({ children, gap = 8 }: { children: ReactNode; gap?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap }}>
      {children}
    </div>
  )
}

/** The next-arrow at the end of a row. */
export const NextArrow = ({ color = C.acc }: { color?: string }) => (
  <span style={{ color, display: 'flex' }}>
    <Icon name="next" size={24} sw={2.5} />
  </span>
)

/** A plain error line under the main action (saving failed). */
export function SaveError({ text }: { text: string | null }) {
  return text ? <Refused text={text} /> : null
}

/** The saving error in words. */
export const saveFailed = (e: unknown) =>
  `${tr('process.saveFailed')}${e instanceof Error && e.message ? ` · ${e.message}` : ''}`

// ---------------------------------------------------------------------------------------------
// Sheets and states
// ---------------------------------------------------------------------------------------------

/** "ऑफ़िस को फ़ोन" / "सुपरवाइज़र": the number on the site list, if there is one. */
export function CallSheet({
  state,
  title,
  onClose,
}: {
  state: ProcessState
  title: string
  onClose: () => void
}) {
  const number = state.pack.settings.quality_contact ?? null
  return (
    <V1Sheet
      title={title}
      onClose={onClose}
      top={380}
      actions={
        <SheetActions>
          <Btn icon="back" onClick={onClose}>
            {tr('gate.v1.back')}
          </Btn>
          {number ? (
            <Cta
              label={tr('process.v1.call.dial')}
              icon="call"
              onClick={() => {
                window.location.href = `tel:${number.replace(/[^\d+]/g, '')}`
              }}
            />
          ) : (
            <WaitCta>{tr('process.v1.call.noNumber')}</WaitCta>
          )}
        </SheetActions>
      }
    >
      <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4 }}>
        {number ? tr('process.v1.call.body', { number }) : tr('process.v1.call.none')}
      </p>
    </V1Sheet>
  )
}

/** No site list on this phone yet: download it once with network. */
export function NoSiteScreen({ loading }: { loading: boolean }) {
  const { online } = useNetworkStatus()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (loading) return <V1Screen nav />
  return (
    <V1Screen
      nav
      header={<PHeader icon="machine" title={tr('process.v1.noPack.title')} />}
      footer={
        <Footer>
          {online ? (
            <Cta
              label={tr('process.v1.noPack.download')}
              icon="download"
              busy={busy}
              onClick={() => {
                setBusy(true)
                setErr(null)
                downloadCaches()
                  .catch((e: unknown) => setErr(saveFailed(e)))
                  .finally(() => setBusy(false))
              }}
            />
          ) : (
            <WaitCta>{tr('process.v1.noPack.needsNet')}</WaitCta>
          )}
        </Footer>
      }
    >
      <Card>
        <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4 }}>{tr('process.v1.noPack.body')}</p>
      </Card>
      <SaveError text={err} />
    </V1Screen>
  )
}
